
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import authService from "../../../services/authService.js";
import { useAdminNotification } from "../../../contexts/AdminNotificationContext";
import { markAllNotificationsRead } from "../../../services/notificationService";

interface AdminHeaderProps {
  /** Controls whether the sidebar is open */
  isSidebarOpen: boolean;
  /** Callback to toggle the sidebar state */
  setIsSidebarOpen: (open: boolean) => void;
  /** Callback to change active tab (e.g. 'contacts') */
  setActiveTab?: (tab: string) => void;
}

/**
 * AdminHeader component – displays the top navigation bar,
 * handles sidebar toggling and user logout.
 */
const AdminHeader: React.FC<AdminHeaderProps> = ({
  isSidebarOpen,
  setIsSidebarOpen,
  setActiveTab,
}) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);
  const { unreadCount, recentNotifications, refreshNotifications } = useAdminNotification();

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
  }, []);

  // Đóng dropdown khi click bên ngoài
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(e.target as Node)) {
        setNotificationOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationToggle = () => {
    setNotificationOpen((prev) => !prev);
  };

  const handleViewAll = () => {
    setNotificationOpen(false);
    setActiveTab?.('contacts');
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      await refreshNotifications();
    } catch {
      // ignore
    }
  };

  const formatTime = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Vừa xong';
    if (diffMins < 60) return `${diffMins} phút trước`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} giờ trước`;
    return d.toLocaleDateString('vi-VN');
  };

  /** Log the admin out safely */
  const handleLogout = async () => {
    if (isLoggingOut) return; // Prevent double click
    
    setIsLoggingOut(true);
    try {
      // Gọi API logout từ authService
      await authService.logout();
      
      navigate("/login");
    } catch (error) {
      // Nếu API logout thất bại, vẫn clear tokens local
      console.error("❌ Logout API failed:", error);
      authService.clearTokens();
      navigate("/login");
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
      <div className="flex items-center justify-between">
        {/* Left side – sidebar toggle & title */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="w-10 h-10 flex items-center justify-center text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Toggle sidebar"
          >
            <i className="ri-menu-line text-xl"></i>
          </button>
        </div>

        {/* Right side – notifications & user menu */}
        <div className="flex items-center gap-4">
          {/* Notifications - dropdown liên hệ mới */}
          <div className="relative" ref={notificationRef}>
            <button
              onClick={handleNotificationToggle}
              className={`relative w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
                notificationOpen ? 'text-[#2A4B7C] bg-gray-100' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
              title="Thông báo"
            >
              <i className="ri-notification-line text-xl"></i>
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 text-xs text-white px-1">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
            </button>

            {notificationOpen && (
              <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden z-50">
                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                  <h3 className="font-semibold text-gray-900">Thông báo</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {unreadCount > 0 ? `${unreadCount} chưa đọc` : 'Không có thông báo mới'}
                  </p>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {recentNotifications.length === 0 ? (
                    <div className="px-4 py-8 text-center text-gray-500 text-sm">
                      <i className="ri-inbox-line text-3xl text-gray-300 mb-2 block"></i>
                      Chưa có thông báo nào
                    </div>
                  ) : (
                    recentNotifications.map((n) => (
                      <button
                        key={n._id}
                        onClick={handleViewAll}
                        className={`w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0 transition-colors ${!n.read ? 'bg-blue-50/50' : ''}`}
                      >
                        <div className="flex gap-3">
                          <div className="w-9 h-9 rounded-full bg-[#00C897]/20 flex items-center justify-center flex-shrink-0">
                            <i className="ri-user-line text-[#00C897]"></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{n.title}</p>
                            {n.meta?.email && (
                              <p className="text-sm text-gray-500 truncate">{n.meta.email}</p>
                            )}
                            {n.meta?.serviceName && (
                              <p className="text-xs text-gray-400 mt-0.5 truncate">{n.meta.serviceName}</p>
                            )}
                            <p className="text-xs text-gray-400 mt-1">{formatTime(n.createdAt)}</p>
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
                {(recentNotifications.length > 0 || unreadCount > 0) && (
                  <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex gap-2">
                    <button
                      onClick={handleViewAll}
                      className="flex-1 py-2 text-sm font-medium text-[#2A4B7C] hover:text-[#1e3a5f]"
                    >
                      Xem tất cả liên hệ
                    </button>
                    {unreadCount > 0 && (
                      <button
                        onClick={handleMarkAllRead}
                        className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700"
                      >
                        Đánh dấu đã đọc
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* User menu */}
          <div className="flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-lg">
            <div className="w-8 h-8 bg-[#2A4B7C] rounded-full flex items-center justify-center">
              <i className="ri-user-line text-white text-sm"></i>
            </div>
            <div className="text-sm">
              <p className="font-medium text-gray-900">
                {user?.userName || 'Admin'}
              </p>
              <p className="text-gray-600">
                {user?.roles?.includes('SuperAdmin') ? 'Super Admin' : 'Quản trị viên'}
              </p>
            </div>
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={isLoggingOut ? "Đang đăng xuất..." : "Đăng xuất"}
            >
              {isLoggingOut ? (
                <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <i className="ri-logout-box-line text-xl"></i>
              )}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default AdminHeader;
